import { Button, Input, Label, Switch } from '@ui';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    GripVertical,
    Plus,
    Trash2,
    X,
    CircleDot,
    CheckSquare,
    Type,
} from 'lucide-react';
import type { SortableQuestionItemProps, QuestionType } from '../types';

export function SortableQuestionItem({
    question,
    onUpdate,
    onDelete,
    onTypeChange,
    onAddOption,
    onDeleteOption,
    onUpdateOption,
}: SortableQuestionItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: question.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="rounded-lg border bg-neutral-50 dark:bg-neutral-800/50 p-4 space-y-3"
        >
            <div className="flex items-start gap-3">
                <div
                    {...attributes}
                    {...listeners}
                    className="flex h-9 w-6 items-center justify-center cursor-grab active:cursor-grabbing text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300"
                >
                    <GripVertical className="h-4 w-4" />
                </div>
                <div className="flex-1 space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1">
                            <Label className="text-xs text-neutral-500 dark:text-neutral-400">질문명</Label>
                            <Input
                                placeholder="입력해 주세요"
                                value={question.name}
                                onChange={(e) => onUpdate(question.id, { name: e.target.value })}
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-neutral-500 dark:text-neutral-400">질문 타입</Label>
                            <select
                                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                value={question.type}
                                onChange={(e) => onTypeChange(question.id, e.target.value as QuestionType)}
                            >
                                <option value="single">단일 선택형</option>
                                <option value="multiple">복수 선택형</option>
                                <option value="text">서술형</option>
                            </select>
                        </div>
                    </div>
                    {/* 단일 선택형 (SINGLE_CHOICE) */}
                    {question.type === 'single' && (
                        <div className="space-y-2 pl-4 border-l-2 border-neutral-200 dark:border-neutral-700">
                            <Label className="text-xs text-neutral-500 dark:text-neutral-400">선택 항목 (1개만 선택 가능)</Label>
                            {question.options.map((option, optIndex) => (
                                <div key={option.id} className="flex items-center gap-2">
                                    <CircleDot className="h-4 w-4 text-neutral-400 dark:text-neutral-500 flex-shrink-0" />
                                    <Input
                                        className="flex-1 h-8"
                                        placeholder={`옵션 ${optIndex + 1}`}
                                        value={option.value}
                                        onChange={(e) => onUpdateOption(question.id, option.id, e.target.value)}
                                    />
                                    {question.options.length > 2 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-neutral-400 hover:text-danger-500"
                                            onClick={() => onDeleteOption(question.id, option.id)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-7"
                                onClick={() => onAddOption(question.id)}
                            >
                                <Plus className="h-3 w-3 mr-1" />
                                항목 추가
                            </Button>
                        </div>
                    )}
                    {/* 복수 선택형 (MULTIPLE_CHOICE) */}
                    {question.type === 'multiple' && (
                        <div className="space-y-2 pl-4 border-l-2 border-blue-200">
                            <Label className="text-xs text-neutral-500 dark:text-neutral-400">선택 항목 (여러 개 선택 가능)</Label>
                            {question.options.map((option, optIndex) => (
                                <div key={option.id} className="flex items-center gap-2">
                                    <CheckSquare className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                    <Input
                                        className="flex-1 h-8"
                                        placeholder={`옵션 ${optIndex + 1}`}
                                        value={option.value}
                                        onChange={(e) => onUpdateOption(question.id, option.id, e.target.value)}
                                    />
                                    {question.options.length > 2 && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 text-neutral-400 hover:text-danger-500"
                                            onClick={() => onDeleteOption(question.id, option.id)}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7"
                                onClick={() => onAddOption(question.id)}
                            >
                                <Plus className="h-3 w-3 mr-1" />
                                항목 추가
                            </Button>
                        </div>
                    )}
                    {/* 서술형 (TEXT) */}
                    {question.type === 'text' && (
                        <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 pl-4 border-l-2 border-neutral-200 dark:border-neutral-700">
                            <Type className="h-4 w-4" />
                            <span>고객이 자유롭게 텍스트를 입력할 수 있습니다.</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                        <span className="text-xs text-neutral-500 dark:text-neutral-400">필수</span>
                        <Switch
                            checked={question.isRequired}
                            onCheckedChange={(checked) => onUpdate(question.id, { isRequired: checked })}
                        />
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-neutral-400 hover:text-danger-500"
                        onClick={() => onDelete(question.id)}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
